import { FC, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import type { IGoodCompare, IOneClickGood } from 'entities/products/model/IGood';
import { useCart } from 'shared/lib/hooks/useCart';
import { useDispatch } from 'shared/lib/hooks/useDispatch';
import { toNumberWithSpaces } from 'shared/lib/formatters';
import { getImageVariantProps } from 'shared/lib/images';
import { setOneClickGood } from 'src/store/goods';
import { metric } from 'features/metric';

import { Icon } from '@nebo-team-dev/vobaza.ui.icon';
import { Button } from '@nebo-team-dev/vobaza.ui.button';
import { IProductError, checkProductToBuy } from 'entities/products/lib/checkProductToBuy';
import CartItemChangeModal from 'components/Cart/Modal/CartItemChangeModal';

import PlaceholderImage from 'assets/images/placeholder_small.png';

import styles from './styles.module.scss';

type Props = {
  good: IGoodCompare;
  deleteGood: (good) => void;
};

const CompareListItem: FC<Props> = ({ good, deleteGood }) => {
  const { addToCart } = useCart(good);
  const [isAvailable, setIsAvailable] = useState(good.is_available);
  const [errorToBuy, setErrorToBuy] = useState<IProductError | null>(null);
  const dispatch = useDispatch();

  const deleteGoodHandler = () => {
    deleteGood(good);
  };

  const openOneClickOrder = async () => {
    const errorToBuy = await checkProductToBuy(good.id);
    setErrorToBuy(errorToBuy);

    if (errorToBuy) return;

    const newGood: IOneClickGood = {
      id: good.id,
      name: good.name,
      price: good.price,
      brand: good.brand,
      quantity: 1,
      main_image: good.main_image,
      sku: good.sku,
      list_price: good.list_price,
      parent_categories: good.parent_categories,
    };

    dispatch(setOneClickGood(newGood));
    metric.reachGoal('buy_with_one_click_form_opened');
  };

  const addToCartHandler = async () => {
    metric.addProduct({ product: good });

    const res = await addToCart({});
    metric.reachGoal('basket');
    if (res?.error) setErrorToBuy(res?.error);
  };

  useEffect(() => {
    setIsAvailable(good.is_available);
  }, [good.is_available]);

  return (
    <div className={styles.compareListItem}>
      {errorToBuy && (
        <CartItemChangeModal
          title={errorToBuy?.title || ''}
          description={errorToBuy?.description || ''}
          onClose={() => {
            setErrorToBuy(null);
            setIsAvailable(false);
          }}
        />
      )}
      <div className={styles.compareListItemTop}>
        <button className={styles.compareListItemDelete} onClick={deleteGoodHandler}>
          <Icon name="Trash" /> Удалить
        </button>
        <div className={styles.compareListItemImage}>
          <Link href={`/product/${good.slug}`} passHref target="_blank">
            {good.main_image ? (
              <Image {...getImageVariantProps(good.main_image.variants, 'medium')} alt={good.name} />
            ) : (
              <Image src={PlaceholderImage} width={278} height={278} alt={good.name} />
            )}
          </Link>
        </div>
        <div className={styles.compareListItemTitle}>
          <Link href={`/product/${good.slug}`} passHref target="_blank">
            {good.seo?.page_name || good.name}
          </Link>
        </div>
        <div className={styles.compareListItemPrices}>
          {good.list_price && (
            <div className={styles.compareListItemOldPrice}>{toNumberWithSpaces(good.list_price)} ₽</div>
          )}
          &nbsp;
          <div className={styles.compareListItemPrice}>{toNumberWithSpaces(good.price)} ₽</div>
        </div>
        {good.store_quantity === 0 && good.on_order && (
          <div className={styles.onOrderLabel}>
            <b>Доступен под заказ</b>
          </div>
        )}
      </div>

      <div className={styles.compareListItemBottom}>
        {isAvailable || good.on_order ? (
          <>
            <div className={styles.compareListItemCartButtons}>
              <Button text="В корзину" onClick={addToCartHandler} disabled={!good.price} />
              {!!good.price && (
                <div>
                  <Button
                    text="Заказать в 1 клик"
                    variation="dashed"
                    style={{ fontSize: '14px' }}
                    onClick={openOneClickOrder}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className={styles.compareListItemCartButtons}>
            <div className={styles.compareListItemCartNotAvailiable}>
              <Icon name={'Cross'} /> Нет в наличии
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default CompareListItem;
